import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, Plus, History, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/Pagination';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useRole } from '@/hooks/useRole';
import { useTimeline, TIMELINE_PAGE_SIZE } from './useTimeline';
import { TimelineItemRow } from './TimelineItemRow';
import { AddEventModal } from './AddEventModal';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  EMPTY_TIMELINE_FILTERS,
  getDefaultTimelineFilters,
  isDefaultTimelineFilters,
} from './types';
import type { TimelineCategory, TimelineFilters, TimelineItem } from './types';

const ALL_CATEGORIES: TimelineCategory[] = [
  'contact_phone',
  'contact_email',
  'contact_whatsapp',
  'meeting',
  'milestone',
  'note',
  'document_upload',
  'transaction_created',
];

function groupByDay(
  items: TimelineItem[],
): Array<{ day: string; date: Date; items: TimelineItem[] }> {
  const buckets = new Map<string, { date: Date; items: TimelineItem[] }>();
  for (const it of items) {
    const dayKey = format(it.date, 'yyyy-MM-dd');
    const existing = buckets.get(dayKey);
    if (existing) {
      existing.items.push(it);
    } else {
      buckets.set(dayKey, { date: it.date, items: [it] });
    }
  }
  return Array.from(buckets.entries())
    .map(([day, { date, items: dayItems }]) => ({ day, date, items: dayItems }))
    .sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function TimelinePage() {
  const { clientId, client } = useCurrentClient();
  const { isStaff } = useRole();
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [filters, setFilters] = useState<TimelineFilters>(getDefaultTimelineFilters);
  const [showFilters, setShowFilters] = useState(false);
  const { data, isLoading, isFetching } = useTimeline(page, filters);
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / TIMELINE_PAGE_SIZE));

  // Volta para página 1 quando cliente ou filtros mudam (totalPages pode encolher)
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setPage(1);
  }, [clientId, filters]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const grouped = useMemo(() => groupByDay(items), [items]);

  const toggleCategory = (cat: TimelineCategory) => {
    setFilters((f) => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter((c) => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const isDefault = isDefaultTimelineFilters(filters);
  const hasActiveFilters = !isDefault;

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">Linha do tempo</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <History className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para ver a linha do tempo.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Linha do tempo</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name ? `${client.name} · ` : ''}
            {isDefault
              ? format(new Date(), "MMMM 'de' yyyy", { locale: ptBR })
              : filters.fromDate || filters.toDate
                ? `Período personalizado`
                : 'Tudo'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            className={hasActiveFilters ? 'border-primary text-primary' : ''}
          >
            <Filter size={14} className="mr-2" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                {filters.categories.length +
                  (filters.fromDate && filters.fromDate !== getDefaultTimelineFilters().fromDate
                    ? 1
                    : 0) +
                  (filters.fromDate === '' ? 1 : 0) +
                  (filters.toDate ? 1 : 0)}
              </span>
            )}
          </Button>
          {isStaff && (
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus size={14} className="mr-2" />
              Adicionar evento
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label className="text-xs">Tipos de evento</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((cat) => {
                  const active = filters.categories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                        active
                          ? CATEGORY_COLORS[cat]
                          : 'border-border bg-white text-muted-foreground hover:bg-secondary'
                      }`}
                    >
                      {CATEGORY_LABELS[cat]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tl-from" className="text-xs">
                  De
                </Label>
                <Input
                  id="tl-from"
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tl-to" className="text-xs">
                  Até
                </Label>
                <Input
                  id="tl-to"
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFilters(EMPTY_TIMELINE_FILTERS)}
                className="h-7 text-xs"
              >
                Ver tudo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFilters(getDefaultTimelineFilters())}
                disabled={isDefault}
                className="h-7 text-xs"
              >
                <X size={12} className="mr-1" />
                Voltar ao mês atual
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <History className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {hasActiveFilters ? 'Nenhum evento corresponde aos filtros.' : 'Sem eventos ainda.'}
            </p>
            {!hasActiveFilters && isStaff && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setAddOpen(true)}>
                <Plus size={14} className="mr-1.5" />
                Registrar primeiro evento
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-6">
            {grouped.map(({ day, date, items: dayItems }) => (
              <div key={day}>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </h3>
                <div>
                  {dayItems.map((it) => (
                    <TimelineItemRow key={it.id} item={it} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-2 pt-2">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            <span className="text-xs text-muted-foreground">
              Página {page} de {totalPages} · {totalCount} evento{totalCount === 1 ? '' : 's'}
              {hasActiveFilters && ' (com filtros)'}
              {isFetching && (
                <Loader2 size={11} className="ml-2 inline animate-spin text-muted-foreground" />
              )}
            </span>
          </div>
        </>
      )}

      <AddEventModal open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
